import {
  Banner,
  BlockStack,
  Box,
  Layout,
} from '@shopify/polaris';
import { useCallback } from 'react';
import { ContentCard } from './ContentCard';
import { MediaCard } from './MediaCard';
import { AudienceCard } from './AudienceCard';
import { ScheduleCard } from './ScheduleCard';
import { ABTestCard } from './ABTestCard';
import { NotificationPreview } from '../preview/NotificationPreview';
import type { FormControls } from '../shared/useNotificationFormState';
import type {
  NotificationTargetType,
  PickerItem,
} from '@/queries/notifications';

export interface ComposeTabProps {
  form: FormControls;
  titleMax: number;
  bodyMax: number;
  products: { items: PickerItem[]; isLoading: boolean };
  collections: { items: PickerItem[]; isLoading: boolean };
  audienceCount: number | null;
  audienceLoading: boolean;
  onRefreshAudienceCount: () => void;
  formErr: string | null;
  okBanner: string | null;
  onDismissFormErr: () => void;
  onDismissOkBanner: () => void;
}

export function ComposeTab(props: ComposeTabProps) {
  const {
    form,
    titleMax,
    bodyMax,
    products,
    collections,
    audienceCount,
    audienceLoading,
    onRefreshAudienceCount,
    formErr,
    okBanner,
    onDismissFormErr,
    onDismissOkBanner,
  } = props;

  const { state, set, effectiveImageUrl } = form;

  const handleTargetTypeChange = useCallback(
    (v: NotificationTargetType) => {
      set('targetType', v);
      set('targetValue', '');
      set('targetThumbnail', '');
      set('targetSearch', '');
    },
    [set],
  );

  const handlePickResource = useCallback(
    (item: PickerItem) => {
      set('targetValue', item.value);
      set('targetThumbnail', item.thumbnail || '');
      set('targetSearch', item.title);
    },
    [set],
  );

  const handleClearTarget = useCallback(() => {
    set('targetValue', '');
    set('targetThumbnail', '');
  }, [set]);

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          {formErr ? (
            <Banner tone="critical" onDismiss={onDismissFormErr}>
              <p>{formErr}</p>
            </Banner>
          ) : null}
          {okBanner ? (
            <Banner tone="success" onDismiss={onDismissOkBanner}>
              <p>{okBanner}</p>
            </Banner>
          ) : null}

          <ContentCard
            title={state.title}
            body={state.body}
            titleMax={titleMax}
            bodyMax={bodyMax}
            onTitleChange={(v) => set('title', v)}
            onBodyChange={(v) => set('body', v)}
          />

          <MediaCard
            imageUrl={state.imageUrl}
            imageCrop={state.imageCrop}
            imageBgEnabled={state.imageBgEnabled}
            imageBgColor={state.imageBgColor}
            targetType={state.targetType}
            targetValue={state.targetValue}
            targetSearch={state.targetSearch}
            targetThumbnail={state.targetThumbnail}
            effectiveImageUrl={effectiveImageUrl}
            products={products}
            collections={collections}
            onImageUrlChange={(v) => set('imageUrl', v)}
            onImageCropChange={(v) => set('imageCrop', v)}
            onImageBgEnabledChange={(v) => set('imageBgEnabled', v)}
            onImageBgColorChange={(v) => set('imageBgColor', v)}
            onTargetTypeChange={handleTargetTypeChange}
            onTargetValueChange={(v) => set('targetValue', v)}
            onTargetSearchChange={(v) => set('targetSearch', v)}
            onPickResource={handlePickResource}
            onClearTarget={handleClearTarget}
          />

          <AudienceCard
            includeIos={state.includeIos}
            includeAndroid={state.includeAndroid}
            includeAr={state.includeAr}
            includeEn={state.includeEn}
            loginStatus={state.loginStatus}
            audienceCount={audienceCount}
            audienceLoading={audienceLoading}
            onIncludeIosChange={(v) => set('includeIos', v)}
            onIncludeAndroidChange={(v) => set('includeAndroid', v)}
            onIncludeArChange={(v) => set('includeAr', v)}
            onIncludeEnChange={(v) => set('includeEn', v)}
            onLoginStatusChange={(v) => set('loginStatus', v)}
            onRefreshAudienceCount={onRefreshAudienceCount}
          />

          <ScheduleCard
            sendMode={state.sendMode}
            scheduleAt={state.scheduleAt}
            cronExpr={state.cronExpr}
            triggerKind={state.triggerKind}
            triggerHours={state.triggerHours}
            abEnabled={state.abEnabled}
            onSendModeChange={(v) => set('sendMode', v)}
            onScheduleAtChange={(v) => set('scheduleAt', v)}
            onCronExprChange={(v) => set('cronExpr', v)}
            onTriggerKindChange={(v) => set('triggerKind', v)}
            onTriggerHoursChange={(v) => set('triggerHours', v)}
          />

          <ABTestCard
            enabled={state.abEnabled}
            title={state.title}
            body={state.body}
            titleB={state.titleB}
            bodyB={state.bodyB}
            splitA={state.splitA}
            titleMax={titleMax}
            bodyMax={bodyMax}
            onEnabledChange={(v) => {
              set('abEnabled', v);
              // A/B locks scheduling to "now"
              if (v && state.sendMode !== 'now') set('sendMode', 'now');
            }}
            onTitleBChange={(v) => set('titleB', v)}
            onBodyBChange={(v) => set('bodyB', v)}
            onSplitAChange={(v) => set('splitA', v)}
          />
        </BlockStack>
      </Layout.Section>

      <Layout.Section variant="oneThird">
        <Box position="sticky" insetBlockStart="500">
          <NotificationPreview
            title={state.title}
            body={state.body}
            imageUrl={effectiveImageUrl}
            imageCrop={state.imageCrop}
            imageBgColor={state.imageBgEnabled ? state.imageBgColor : undefined}
          />
        </Box>
      </Layout.Section>
    </Layout>
  );
}
